import React, {useState, useEffect, useRef} from 'react';
import {Button, Card, Col, Row, Select, Spin} from 'antd';
import PropTypes from 'prop-types';

import {getAccessories} from '../../../helpers/accessory';
import {messageType, showMessages} from '../../../helpers/message';
import {syncAccessories} from '../../../helpers/car';
import {isTwoArrayIdentical} from '../../../helpers/misc';

import './CarAccessories.less';
import {cardsGridColumn} from '../../../assets/styles';
import {darkBlue} from '../../../assets/colors';
import {nothingToUpdate} from '../../../helpers/text';
import {useAppDispatch} from '../../../hooks/useStore';
import {apiSlice} from '../../../store/api/apiSlice';

const CarAccessories = ({car}) => {
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);
    const dispatch = useAppDispatch();

    const [loading, setLoading] = useState(false);

    const [selectedAccessories, setSelectedAccessories] = useState(car.accessories.map(accessory => accessory.id));
    const [availableAccessories, setAvailableAccessories] = useState([]);
    const existingAccessories = car.accessories.map(accessory => accessory.name);

    useEffect(() => {
        const fetchAccessories = async () => {
            const abortSignal = abortSignalRef.current;
            setLoading(true);

            try {
                const response = await getAccessories(1, 99999, abortSignal);

                if (abortSignal.aborted === false && response) {
                    setAvailableAccessories(response.data)
                }

                setLoading(false);
            } catch (error) {
                showMessages(error.message, messageType.error);
                setLoading(false);
                if(error.status_code === 404) window.location.replace('/404');
            }

      }

      fetchAccessories();

    },[]);

    const handleOnChange = (value, option) => {
        const selected = option.map(opt => opt.id);
        setSelectedAccessories(selected);
    }

    const handleOnSave = async () => {
        const carID = car.id;

        const result = sanityCheck();

        if (result === false) {
            showMessages(nothingToUpdate, messageType.error);
            return;
        }

        try {
            const abortSignal = abortSignalRef.current;
            setLoading(true)

            const response = await syncAccessories(carID, selectedAccessories, abortSignal);

            if (abortSignal.aborted === false && response) {
                // Temp invalidate Car until refactored to use RTK.
                dispatch(apiSlice.util.invalidateTags([{type: 'Car', id: carID}]));
                showMessages('Accessories have successfully been saved.', messageType.success);
            }

            setLoading(false);
        } catch (error) {
            showMessages(error.message, messageType.error);
            setLoading(false);
        }
    }

    const sanityCheck = () => {
        // Check whether selected accessories have been modified.
        const existingAccessories = car.accessories.map(accessory => accessory.id)
        return !isTwoArrayIdentical(selectedAccessories, existingAccessories);
    }

    return (
        <Row>
            <Col {...cardsGridColumn}>
                <Spin spinning={loading}>
                    <Card>
                        <Card.Meta
                            title={<span style={{color: darkBlue}}>Vehicle Accessories</span>}
                            description={<span>This is where a vehicle's accessories can be managed. You can select or search for vehicle accessories from the menu below.</span>}
                        />
                        <Select
                            mode="multiple"
                            className="accessories-select"
                            placeholder="Select or search for accessories"
                            autoFocus={true}
                            defaultValue={existingAccessories}
                            onChange={handleOnChange}
                        >
                            {
                                availableAccessories.map(accessory => (
                                    <Select.Option key={accessory.name} value={accessory.name} id={accessory.id}>
                                        {accessory.name}
                                    </Select.Option>
                                ))
                            }
                        </Select>
                        <Row type="flex" justify="end" className="accessories-save-button">
                            <Button
                                type="primary"
                                size="large"
                                onClick={handleOnSave}
                            >
                                Save
                            </Button>
                        </Row>
                    </Card>
                </Spin>
            </Col>
        </Row>
    )
}

export default CarAccessories;

CarAccessories.propTypes = {
    car: PropTypes.object.isRequired,
}
